// src/pages/LeaderLab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebaseConfig'; // Import auth and db
import { collection, addDoc } from "firebase/firestore"; 
import { useSearchParams, useNavigate } from 'react-router-dom'; // Added useNavigate
import Scheduler from '../components/Scheduler';

const LeaderLab = () => {
  const navigate = useNavigate(); // Hook for navigation if needed
  const [currentStep, setCurrentStep] = useState('initial-cta-section');
  const [chosenProfession, setChosenProfession] = useState('');
  const [isOtherProfession, setIsOtherProfession] = useState(false);
  const [quizResults, setQuizResults] = useState({ correct: 0, total: 0 });
  const [showSplash, setShowSplash] = useState(true);
  const [ctaIndex, setCtaIndex] = useState(0);
  const [referrerId, setReferrerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [searchParams] = useSearchParams();
  

  // --- CTA Data Definition ---
  const ctaData = [
    { text: "Can you manage global logistics in a crisis?", color: "bg-blue-600" },
    { text: "Can you explain a complex financial product to a client?", color: "bg-red-600" },
    { text: "Can you manage a team in a high-risk excavation?", color: "bg-yellow-600" },
    { text: "Can you diagnose a rare condition under pressure?", color: "bg-green-600" },
    { text: "Can you coordinate an offshore drilling operation?", color: "bg-gray-900" },
    { text: "Can you handle an emergency landing in English?", color: "bg-purple-600" },
    { text: "Can you build client loyalty with your expertise?", color: "bg-orange-600" },
    { text: "Can you negotiate a high-stakes corporate merger?", color: "bg-pink-600" },
    { text: "Can you manage a difficult customer complaint in a hotel?", color: "bg-teal-600" },
    { text: "Can you lead a crew and ensure passenger safety?", color: "bg-indigo-600" }
  ];
  const currentCta = ctaData[ctaIndex];


  // --- Effects ---

  // 1. Splash Screen Timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  
  // 2. CTA Rotation (Uses React State, NO DOM MANIPULATION)
  useEffect(() => {
    if (!showSplash && currentStep === 'initial-cta-section') {
      const interval = setInterval(() => {
        setCtaIndex(prev => (prev + 1) % ctaData.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [showSplash, currentStep, ctaData.length]);

  // 3. Referrer ID Capture
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferrerId(ref);
      localStorage.setItem('referrerId', ref);
    } else {
      const storedRef = localStorage.getItem('referrerId');
      if(storedRef) setReferrerId(storedRef);
    }
  }, [searchParams]);

  // --- Data & Mappings ---
  const simulatorLinks = {
    'Logistics Coordinator': 'https://aulice-logistics-simulator.netlify.app/',
    'Banking Professionals': 'https://aulice-banking-simulator.netlify.app/',
    'Mining Engineers': 'https://aulice-mining-simulator.netlify.app/',
    'Medical Professionals': 'https://aulice-medical-simulator.netlify.app/',
    'Oil & Gas Professionals': 'https://aulice-oil-gas-simulator.netlify.app/',
    'Pilot & ATCs': 'https://aulice-pilot-atc-simulator.netlify.app/',
    'Aestheticians': 'https://aulice-aesthetician-simulator.netlify.app/',
    'Legal Professionals': 'https://aulice-legal-simulator.netlify.app',
    'Hotel & Hospitality Professionals': 'https://aulice-hotel-simulator.netlify.app/',
    'Cabin Crew Professionals': 'https://aulice-cabin-crew-simulator.netlify.app/'
  };

  const quizData = {
    professions: [
      // Full list of professions needed for mapping
      { name: 'Logistics Coordinator', q: [
        { question: 'We need to track the ... of the shipment to ensure a timely delivery.', answer: 'status' },
        { question: 'The team will ... the inventory to ensure accuracy.', answer: 'verify' },
        { question: 'Please provide an ... of the current stock levels.', answer: 'overview' }
      ] },
      // ... rest of professions (shortened for clarity)
    ],
    generic: [
      { question: 'The team needs to be more ... when facing unexpected challenges.', answer: 'flexible' },
      { question: 'Our new leader is excellent at providing clear and effective ... to the team.', answer: 'feedback' },
      { question: 'In order to build consensus, we must first find a common ... for the team to agree on.', answer: 'ground' },
    ]
  };
  
  // Consolidate quiz data into a map for easy access
  const professionQuizMap = quizData.professions.reduce((acc, curr) => {
    acc[curr.name] = curr.q;
    return acc;
  }, {
    'Logistics Coordinator': quizData.professions.find(p => p.name === 'Logistics Coordinator').q, // Example structure
    // ... all other professions should be mapped here
    'Banking Professionals': quizData.generic, // Placeholder data mapping
    'Mining Engineers': quizData.generic,
    'Medical Professionals': quizData.generic,
    'Oil & Gas Professionals': quizData.generic,
    'Pilot & ATCs': quizData.generic,
    'Aestheticians': quizData.generic,
    'Legal Professionals': quizData.generic,
    'Hotel & Hospitality Professionals': quizData.generic,
    'Cabin Crew Professionals': quizData.generic,
  });

  const steps = ['initial-cta-section', 'profession-section', 'simulator-link-section', 'other-simulator-option-section', 'quiz-section', 'form-section', 'scheduling-section','final-section', 'macro-gateway-section'];
  
  const [formDataObj, setFormDataObj] = useState({ name: '', contact: '', goal: '', customGoal: '', manualProfession: '' });
  const [quizAnswers, setQuizAnswers] = useState({});

  // --- Handlers ---
  const handleProfessionChange = (e) => {
    const value = e.target.value;
    setChosenProfession(value);
    setIsOtherProfession(value === 'Other');
  };

  const handleProfessionSubmit = () => {
    if (!chosenProfession) return;

    if (chosenProfession === 'Other') {
      setCurrentStep('other-simulator-option-section');
    } else {
      setCurrentStep('simulator-link-section');
    }
  };

  const populateQuiz = (isOther = false) => {
    const questions = isOther ? quizData.generic : (professionQuizMap[chosenProfession] || quizData.generic);
    const shuffled = [...questions].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, Math.min(questions.length, 3));
    
    setQuizQuestions(selected);
    setCurrentStep('quiz-section');
  };

  const handleQuizInputChange = (index, value) => {
    setQuizAnswers(prev => ({ ...prev, [index]: value }));
  };

  const handleFormInputChange = (e) => {
    const { id, value } = e.target;
    setFormDataObj(prev => ({ ...prev, [id]: value }));
  };


  const handleQuizSubmit = (e) => {
    e.preventDefault();
    let correctCount = 0;
    const inputs = document.querySelectorAll('#quiz-questions input'); 
  
    quizQuestions.forEach((q, index) => {
        const userAnswer = (quizAnswers[index] || "").trim().toLowerCase();
        const correctAnswer = q.answer.toLowerCase();
        const inputEl = inputs[index];
         if (userAnswer === correctAnswer) {
            correctCount++;
            inputEl.classList.remove('border-red-500', 'ring-red-500');
            inputEl.classList.add('border-green-500', 'ring-2', 'ring-green-500');
         } else {
            inputEl.classList.remove('border-green-500', 'ring-green-500');
            inputEl.classList.add('border-red-500', 'ring-2', 'ring-red-500');
         }
    });

    setQuizResults({ correct: correctCount, total: quizQuestions.length });
    setCurrentStep('form-section');
  };

  // ✅ Firebase Write Function
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
        await addDoc(collection(db, "leaderLabLeads"), {
            ...formDataObj,
            profession: chosenProfession === 'Other' ? formDataObj.manualProfession : chosenProfession,
            quizResults: quizResults,
            referrerId: referrerId || localStorage.getItem('referrerId') || null,
            submittedAt: new Date(),
            leadSource: "LeaderLab" // ✅ This tag is now added
        });
        setCurrentStep('scheduling-section'); 
      } catch (error) {
          // ... error handling
      } finally {
          setIsLoading(false);
      }
    };
  
  const handleShareClick = () => {
    const uid = auth.currentUser?.uid || 'AULICE';
    const url = `https://aulice-hub.web.app/leaderlab?ref=${referrerId || uid}`;
    navigator.clipboard.writeText(url).then(() => {
      const msg = document.getElementById('share-message');
      if(msg) {
        msg.classList.remove('hidden');
        setTimeout(() => msg.classList.add('hidden'), 2000);
      }
    });
  };

  const goToPrevStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) { 
      setCurrentStep(steps[currentIndex - 1]);
    }
  };


  // --- Render Logic ---

  const renderStep = useCallback(() => {
  
    switch (currentStep) {
        case 'initial-cta-section':
        // The CTA rotation logic is complex, we display it as a full-screen overlay for impact
        // The component returns the full overlay in the main render block
            return (
                <div className="text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Level Up Your Professional English</h1>
                    <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto">Discover how your professional language skills measure up with our quick and interactive Leader Lab. It's free, fast, and tailored just for you.</p>
                    <button
                      onClick={() => setCurrentStep('profession-section')}
                      className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-full shadow-lg hover:bg-blue-700 transition duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Start Your Leader Lab Challenge
                    </button>
                </div>
            );
        case 'profession-section':
            return (
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">What's Your Profession?</h2>
                <div className="space-y-4">
                  <p className="text-gray-600">Choose your field to get a tailored assessment:</p>
                  <select
                    id="profession-select"
                    value={chosenProfession}
                    onChange={handleProfessionChange}
                    className="w-full md:w-2/3 lg:w-1/2 mx-auto p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a Profession</option>
                    <option value="Logistics Coordinator">Logistics Coordinator</option>
                    <option value="Banking Professionals">Banking Professionals</option>
                    <option value="Mining Engineers">Mining Engineers</option>
                    <option value="Medical Professionals">Medical Professionals</option>
                    <option value="Oil & Gas Professionals">Oil & Gas Professionals</option>
                    <option value="Pilot & ATCs">Pilot & ATCs</option>
                    <option value="Aestheticians">Aestheticians</option>
                    <option value="Legal Professionals">Legal Professionals</option>
                    <option value="Hotel & Hospitality Professionals">Hotel & Hospitality Professionals</option>
                    <option value="Cabin Crew Professionals">Cabin Crew Professionals</option>
                    <option value="Other">Other</option>
                  </select>
                  {isOtherProfession && (
                    <div id="other-profession-input" className="space-y-2 mt-4">
                      <label htmlFor="manualProfession" className="block text-sm font-medium text-gray-700 text-left">Insert the name of your profession</label>
                      <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg shadow-sm">
                        <p className="text-sm font-medium">We'll create you a custom-tailored course on demand during the free class!</p>
                      </div>
                      <input
                        type="text"
                        id="manualProfession"
                        value={formDataObj.manualProfession}
                        onChange={handleFormInputChange}
                        required
                        className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                  <button
                    onClick={handleProfessionSubmit}
                    className="w-full md:w-2/3 lg:w-1/2 mx-auto py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition duration-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            );

        case 'simulator-link-section':
            return (
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Explore Your Professional Simulator</h2>
                <p className="text-gray-600 mb-6 max-w-lg mx-auto">Click below to experience a real-world scenario tailored to your profession. It's a quick preview of our immersive learning.</p>
                <a
                  href={simulatorLinks[chosenProfession]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-4 bg-blue-600 text-white font-semibold rounded-full shadow-lg hover:bg-blue-700 transition duration-300 transform hover:scale-105"
                >
                  Launch Simulator
                </a>
                <p className="text-sm text-gray-500 mt-4">This will open in a new tab.</p>
                <button
                  onClick={() => populateQuiz()}
                  className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-300 mt-8"
                >
                  Continue to Assessment
                </button>
              </div>
            );

        case 'other-simulator-option-section':
            return (
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Your Simulator Will Be Like This...</h2>
                <p className="text-lg text-gray-600 mb-8 text-center">Here are a few examples of our professional simulators. Your custom-tailored simulator and course will be created for you during your free class.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <a
                    href="https://aulice.ca/banking/demo/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg shadow-sm hover:bg-gray-100 transition duration-300"
                  >
                    <span className="text-4xl mb-2">💼</span>
                    <p className="text-sm font-semibold">Banking Professionals</p>
                  </a>
                  <a
                    href="https://aulice.ca/aviation/demo/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg shadow-sm hover:bg-gray-100 transition duration-300"
                  >
                    <span className="text-4xl mb-2">✈️</span>
                    <p className="text-sm font-semibold">Pilot & ATCs</p>
                  </a>
                  <a
                    href="https://aulice.ca/hotel/demo/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg shadow-sm hover:bg-gray-100 transition duration-300"
                  >
                    <span className="text-4xl mb-2">🏨</span>
                    <p className="text-sm font-semibold">Hotel & Hospitality</p>
                  </a>
                </div>
                <p className="text-lg text-gray-600 mt-8 text-center">Now, let's proceed with a quick assessment to get a sense of your professional vocabulary.</p>
                <div className="text-center mt-6">
                  <button
                    onClick={() => populateQuiz(true)}
                    className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-300"
                  >
                    Continue to Assessment
                  </button>
                </div>
              </div>
            );

        case 'quiz-section':
            return (
              <div>
                <h2 id="quiz-heading" className="text-3xl font-bold text-gray-800 mb-6 text-center">
                  {isOtherProfession ? "Quick Professional English Assessment" : `Quick ${chosenProfession} Challenge`}
                </h2>
                <form id="quiz-questions" onSubmit={handleQuizSubmit} className="space-y-6 mb-6">
                  {quizQuestions.map((q, index) => (
                    <div key={index}>
                      <p className="mb-2 text-gray-700">{index + 1}. {q.question}</p>
                      <input
                        type="text"
                        onChange={(e) => handleQuizInputChange(index, e.target.value)}
                        required
                        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  ))}
                   <div className="text-center">
                    <button
                        type="submit"
                        className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-300"
                    >
                        Check My Answers
                    </button>
                   </div>
                </form>
              </div>
            );

        case 'form-section':
            return (
              <div>
                <div id="quiz-result-message" className="text-center p-4 rounded-lg mb-6 bg-green-100 text-green-700">
                  You got {quizResults.correct} out of {quizResults.total} correct! Congratulations, you've earned a free assessment.
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Unlock Your Free Class Assessment</h2>
                <form id="user-info-form" onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      value={formDataObj.name}
                      onChange={handleFormInputChange}
                      required
                      className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact" className="block text-sm font-medium text-gray-700">Email Address</label>
                    <input
                      type="email"
                      id="contact"
                      value={formDataObj.contact}
                      onChange={handleFormInputChange}
                      required
                      className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="goal" className="block text-sm font-medium text-gray-700">Your Current Goal</label>
                    <select
                      id="goal"
                      value={formDataObj.goal}
                      onChange={handleFormInputChange}
                      required
                      className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a Goal</option>
                      <option value="promotion">Promotion</option>
                      <option value="relocation">Relocation</option>
                      <option value="test">Language Test</option>
                      <option value="upgrade">Skill Upgrade</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {formDataObj.goal === 'other' && (
                     <div>
                        <label htmlFor="customGoal" className="block text-sm font-medium text-gray-700">Describe your goal</label>
                        <textarea id="customGoal" value={formDataObj.customGoal} onChange={handleFormInputChange} rows="3" className="mt-1 block w-full p-3 border border-gray-300 rounded-lg"></textarea>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition duration-300 disabled:bg-blue-300"
                  >
                    {isLoading ? 'Submitting...' : 'Submit & Continue'}
                  </button>
                </form>
              </div>
            );
            case 'final-section':
              return (
                <div className="text-center py-10">
                  {/* Success Icon */}
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-4xl">🎉</span>
                  </div>
  
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">You're All Set!</h2>
                  <p className="text-lg text-gray-600 mb-2">
                      Your 1:1 Consultation has been successfully booked.
                  </p>
                  <p className="text-gray-500 mb-8 max-w-md mx-auto">
                      We have sent a confirmation email to <strong>{formDataObj.contact}</strong> with your Zoom link. Please check your inbox (and spam folder) for details.
                  </p>
  
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6 max-w-lg mx-auto mb-8 text-left">
                      <h3 className="font-bold text-indigo-900 mb-2">Next Steps:</h3>
                      <ul className="space-y-3 text-sm text-indigo-800">
                          <li className="flex items-start gap-2">
                              <span>📩</span> Check your email for the calendar invite and Zoom link.
                          </li>
                          <li className="flex items-start gap-2">
                              <span>📝</span> Review your quiz results in the email to prepare questions.
                          </li>
                          <li className="flex items-start gap-2">
                              <span>⏰</span> Please join the Zoom call 2-3 minutes early.
                          </li>
                      </ul>
                  </div>
  
                  <div className="mt-6">
                    <button
                      onClick={handleShareClick}
                      className="px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg shadow-md hover:bg-gray-300 transition duration-300 flex items-center gap-2 mx-auto"
                    >
                      <span>🔗</span> Share LeaderLab with a friend
                    </button>
                    <p id="share-message" className="text-sm text-green-600 mt-2 hidden">Link copied to clipboard!</p>
                  </div>
                </div>
              );

              case 'scheduling-section':
              return (
                  <div className="flex flex-col items-center justify-center min-h-[700px] p-4 relative">
                      {/* Background Decoration */}
                      <div className="absolute inset-0 bg-gray-50 z-0">
                          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(#4F46E5 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                      </div>
  
                      <div className="relative z-10 w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col h-[800px]">
                          
                          {/* 1. BRANDED HEADER */}
                          <div className="bg-gradient-to-r from-slate-900 to-indigo-900 p-6 text-white flex flex-col md:flex-row items-center justify-between shadow-md shrink-0">
                              <div className="flex items-center gap-4 mb-4 md:mb-0">
                                  {/* Logo Circle */}
                                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-indigo-900 font-bold text-2xl shadow-inner">
                                      A
                                  </div>
                                  <div>
                                      <h2 className="text-2xl font-bold tracking-tight">Aulice Academy</h2>
                                      <p className="text-indigo-200 text-sm">Free Strategy Consultation</p>
                                  </div>
                              </div>
                              <div className="text-center md:text-right">
                                  <div className="text-sm font-medium opacity-90">Student:</div>
                                  <div className="text-lg font-bold text-white">{formDataObj.name}</div>
                              </div>
                          </div>
  
                          {/* 2. INSTRUCTION BAR */}
                          <div className="bg-indigo-50 p-3 text-center border-b border-indigo-100 shrink-0">
                              <p className="text-indigo-800 text-sm font-medium flex items-center justify-center gap-2">
                                  <span>👇</span> Please select a <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded border border-green-200 font-bold uppercase text-xs">GREEN SLOT</span> to book your time.
                              </p>
                          </div>
  
                          {/* 3. SCHEDULER COMPONENT */}
                          <div className="flex-1 overflow-hidden p-4 bg-white">
                              <Scheduler 
                                  user={{ 
                                      uid: "guest", 
                                      displayName: formDataObj.name, 
                                      email: formDataObj.contact 
                                  }} 
                                  role="guest" 
                                  onBookingComplete={() => setCurrentStep('final-section')}
                              />
                          </div>
                      </div>
  
                      {/* Footer / Timezone Note */}
                      <p className="mt-4 text-gray-400 text-xs relative z-10">
                          All times are displayed in your local timezone.
                      </p>
                  </div>
              );

      case 'macro-gateway-section':
        return (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Your Monthly Learning Plan</h2>
            <p className="text-lg text-gray-600 mb-8 text-center">Here is your full schedule for the month. Each unit is designed to build on the last, ensuring you master every skill.</p>
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold text-blue-800 mb-2">Week 1: Foundational Communication</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Lesson 1.1: Master Class: Pitching Your Ideas</li>
                  <li>Lesson 1.2: Grammar Lab: Advanced Tense Usage</li>
                  <li>Unit 1.3: Vocabulary Builder: Business Finance Terms</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold text-blue-800 mb-2">Week 2: Advanced Negotiation Skills</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Lesson 2.1: Master Class: Conflict Resolution & Persuasion</li>
                  <li>Lesson 2.2: Grammar Lab: Subjunctive Mood for Proposals</li>
                  <li>Unit 2.3: Vocabulary Builder: Legal & Contract Terms</li>
                </ul>
              </div>
              {/* Other weeks... */}
            </div>
          </div>
        );

      default:
        return null;
    }
  }, [currentStep,formDataObj, chosenProfession, isOtherProfession, quizQuestions, quizAnswers, quizResults, formDataObj, isLoading, handleQuizSubmit, handleFormSubmit, handleQuizInputChange, handleFormInputChange, handleShareClick]);


  // Logic for the CTA text fade effect (Removed for stability)

  return (
    <div className="font-sans antialiased bg-gray-100 min-h-screen p-4">
      {/* The CTA overlay logic is removed here to prevent complex DOM manipulation crashes */}
      
      {/* We render the first step directly if it's the CTA or the main app content */}
      {(currentStep === 'initial-cta-section' || currentStep === 'profession-section') ? (
        <div 
          className={`w-full h-full flex items-center justify-center transition-colors duration-1000 ${currentCta.color || 'bg-gray-50'}`}
          style={{ minHeight: '100vh' }}
        >
          <div className="text-center p-8 max-w-lg mx-auto">
            {currentStep === 'initial-cta-section' ? (
                <div className="text-white">
                    <h1 className="text-4xl md:text-5xl font-bold max-w-lg mx-auto leading-snug">
                       {currentCta.text}
                    </h1>
                    <p className="mt-4 text-xl font-medium">Click to continue 👇</p>
                    <button
                      onClick={() => setCurrentStep('profession-section')}
                      className="mt-6 px-8 py-4 bg-white text-blue-600 font-semibold rounded-full shadow-lg hover:bg-gray-100 transition duration-300"
                    >
                      Start Your Leader Lab Challenge
                    </button>
                </div>
            ) : (
                <div className="bg-white p-8 rounded-2xl shadow-xl">
                    {renderStep()}
                </div>
            )}
          </div>
        </div>
      ) : (
        // Renders the rest of the application content
        <div
          id="app"
          className={`container bg-white p-8 md:p-12 rounded-2xl shadow-xl space-y-8 `}
        >
          <nav id="app-navigation" className="flex justify-between items-center mb-6">
            <button
              onClick={goToPrevStep}
              className="text-blue-600 hover:text-blue-800 transition duration-300"
            >
              &larr; Back
            </button>
            <span className="text-sm font-medium text-gray-500">
              {/* Simple step counter */}
            </span>
          </nav>
          {renderStep()}
        </div>
      )}
    </div>
  );
};

export default LeaderLab;